import { useState, useMemo } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import IntegrationCard from "./IntegrationCard";
import { integrations, integrationCategories } from "../data/integrations";

import { useTranslation } from 'react-i18next';
export default function IntegrationGrid({ onConnect, onDisconnect, onConfigModal }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeStatus, setActiveStatus] = useState("all");

  const filteredIntegrations = useMemo(() => {
    let result = [...integrations];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.tagline.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q)
      );
    }

    // Category filter
    if (activeCategory !== "all") {
      result = result.filter((i) => i.category === activeCategory);
    }

    // Status filter
    if (activeStatus === "connected") {
      result = result.filter((i) => i.status === "connected");
    } else if (activeStatus === "available") {
      result = result.filter((i) => i.status === "available");
    }

    return result;
  }, [searchQuery, activeCategory, activeStatus]);

  const connectedCount = integrations.filter((i) => i.status === "connected").length;

  const handleConnect = (id) => {
    if (onConfigModal) {
      onConfigModal(id);
    } else if (onConnect) {
      onConnect(id);
    }
  };

  return (
    <div>
      {/* Search bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('integration_grid.search_integrations')}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg pl-11 pr-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
        {/* Category pills */}
        <div className="flex flex-wrap items-center gap-2">
          <SlidersHorizontal className="w-3.5 h-3.5 text-zinc-500 mr-1" />
          <button
            onClick={() => setActiveCategory("all")}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              activeCategory === "all"
                ? "border-amber-500 text-amber-500 bg-amber-500/5"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            All
          </button>
          {integrationCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                activeCategory === cat.id
                  ? "border-amber-500 text-amber-500 bg-amber-500/5"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            onClick={() => setActiveStatus("all")}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              activeStatus === "all"
                ? "border-zinc-600 text-zinc-200"
                : "border-zinc-800 text-zinc-500 hover:border-zinc-700"
            }`}
          >
            All ({integrations.length})
          </button>
          <button
            onClick={() => setActiveStatus("connected")}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              activeStatus === "connected"
                ? "border-amber-500 text-amber-500 bg-amber-500/5"
                : "border-zinc-800 text-zinc-500 hover:border-zinc-700"
            }`}
          >
            Connected ({connectedCount})
          </button>
          <button
            onClick={() => setActiveStatus("available")}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              activeStatus === "available"
                ? "border-zinc-500 text-zinc-300"
                : "border-zinc-800 text-zinc-500 hover:border-zinc-700"
            }`}
          >
            Available ({integrations.length - connectedCount})
          </button>
        </div>
      </div>

      {/* Grid */}
      {filteredIntegrations.length === 0 ? (
        <div className="border border-zinc-800 rounded-xl p-12 text-center">
          <p className="text-zinc-500 font-mono text-sm">{t('integration_grid.no_integrations_match_your_filters')}</p>
          <button
            onClick={() => {
              setSearchQuery("");
              setActiveCategory("all");
              setActiveStatus("all");
            }}
            className="mt-4 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-[0.2em]"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredIntegrations.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onConnect={handleConnect}
              onDisconnect={onDisconnect}
            />
          ))}
        </div>
      )}

      {/* Results count */}
      <div className="mt-6 text-right">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Showing {filteredIntegrations.length} of {integrations.length} integrations
        </span>
      </div>
    </div>
  );
}
